import type { ProfileConfig } from "@worker/config-schema";
import { el } from "../../lib/dom";
import { widgetShell } from "./base";

type ClockWidget = Extract<ProfileConfig["widgets"][number], { type: "clock" }>;

/**
 * Local-time clock. Client-only — no network. Ticks every second.
 *
 * When `syncWithInline` is true, the widget inherits format + showSeconds
 * from the profile's inlineClock config so both clocks look identical.
 * Flip off to set them independently via the widget's own fields.
 */
export function mountClockWidget(
  cfg: ClockWidget,
  inline: ProfileConfig["profile"]["inlineClock"],
): HTMLElement {
  const shell = widgetShell({
    kind: "clock",
    title: cfg.label ?? cfg.timezone,
  });

  const sync = cfg.syncWithInline;
  const format = sync ? inline.format : cfg.format;
  const showSeconds = sync ? inline.showSeconds : cfg.showSeconds;

  let formatter: Intl.DateTimeFormat;
  try {
    formatter = new Intl.DateTimeFormat(undefined, {
      timeZone: cfg.timezone,
      hour: "2-digit",
      minute: "2-digit",
      ...(showSeconds ? { second: "2-digit" as const } : {}),
      hour12: format === "12",
    });
  } catch {
    shell.setError(`bad timezone: ${cfg.timezone}`);
    return shell.root;
  }

  const display = el("div", { class: "widget-clock__time" });
  const date = el("div", { class: "widget-clock__date" });
  shell.body.innerHTML = "";
  shell.body.append(display, date);

  let dateFmt: Intl.DateTimeFormat;
  try {
    dateFmt = new Intl.DateTimeFormat(undefined, {
      timeZone: cfg.timezone,
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  } catch {
    dateFmt = new Intl.DateTimeFormat();
  }

  const tick = () => {
    const now = new Date();
    display.textContent = formatter.format(now);
    date.textContent = dateFmt.format(now);
  };
  tick();
  // Poll every second when seconds visible, otherwise every 15s to avoid
  // wasting a rAF budget the user can't see.
  const interval = window.setInterval(tick, showSeconds ? 1000 : 15_000);
  shell.root.addEventListener("bio:teardown", () => window.clearInterval(interval));

  return shell.root;
}
